import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'

interface NationalityChartProps {
  data: {
    swiss: number
    foreign: number
  }
  height?: number
  t: TFunction
}

export interface NationalityChartRef {
  exportChart: () => string | null
}

export const NationalityChart = forwardRef<
  NationalityChartRef,
  NationalityChartProps
>(({ data, height = 300, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Prepare nationality data
    const totalPopulation = data.swiss + data.foreign
    const swissLabel = t('charts.nationalityChart.legend.swiss')
    const foreignLabel = t('charts.nationalityChart.legend.foreign')
    const nationalityData = [
      {
        name: swissLabel,
        value: data.swiss,
        percentage:
          totalPopulation > 0
            ? ((data.swiss / totalPopulation) * 100).toFixed(1)
            : '0',
        key: 'swiss',
      },
      {
        name: foreignLabel,
        value: data.foreign,
        percentage:
          totalPopulation > 0
            ? ((data.foreign / totalPopulation) * 100).toFixed(1)
            : '0',
        key: 'foreign',
      },
    ]

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: function (params: any) {
          const item = params.data
          const marketImplication =
            item.key === 'swiss'
              ? t('charts.nationalityChart.tooltips.swissImplication')
              : t('charts.nationalityChart.tooltips.foreignImplication')

          return `
            <div style="text-align: left;">
              <strong>${item.name} ${t('charts.nationalityChart.tooltips.population')}</strong><br/>
              <span style="color: ${params.color};">● ${item.value.toLocaleString()}</span><br/>
              <span style="color: ${params.color};">● ${item.percentage}%</span><br/>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <strong>${t('charts.nationalityChart.tooltips.housingImpact')}:</strong><br/>
                <span style="color: #6b7280;">${marketImplication}</span>
              </div>
            </div>
          `
        },
      },
      legend: {
        orient: 'horizontal',
        left: 'center',
        top: '5%',
        data: [swissLabel, foreignLabel],
        selectedMode: false,
        textStyle: {
          fontSize: 12,
          fontWeight: 'bold',
        },
      },
      series: [
        {
          name: t('charts.series.nationality'),
          type: 'pie',
          radius: ['30%', '60%'],
          center: ['50%', '60%'],
          selectedMode: false,
          data: nationalityData.map((item, index) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
            key: item.key,
            itemStyle: {
              color: index === 0 ? '#ef4444' : '#3b82f6', // Red for Swiss, Blue for Foreign
            },
          })),
          label: {
            formatter: function (params: any) {
              return `${params.data.name}\n${params.data.percentage}%`
            },
            fontSize: 11,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '250px',
      }}
    />
  )
})

NationalityChart.displayName = 'NationalityChart'
